/** Display formatting. Pure, and deliberately locale-free.
 *
 * `toLocaleString` is banned in the kernel because it is ambient
 * nondeterminism; it is avoided here too, for a duller reason - a screenshot
 * test that renders "1,000" on one machine and "1 000" on another is a test
 * that fails for no reason.
 */

import type { Resources } from "@game/core";
import { RESOURCES } from "@game/core";

/** 1234567 -> "1,234,567". Negative numbers keep the sign outside the groups. */
export function groupDigits(n: number): string {
  const negative = n < 0;
  const digits = String(Math.abs(Math.trunc(n)));
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ",";
    out += digits.charAt(i);
  }
  return negative ? `-${out}` : out;
}

/** Army sizes are shown classic-style once they stop fitting: 1.2k, 34k, 1.1M. */
export function compactCount(n: number): string {
  const v = Math.trunc(n);
  if (v < 10000) return groupDigits(v);
  if (v < 1000000) {
    const k = Math.floor(v / 100) / 10;
    return `${k % 1 === 0 ? String(k) : k.toFixed(1)}k`;
  }
  const m = Math.floor(v / 100000) / 10;
  return `${m % 1 === 0 ? String(m) : m.toFixed(1)}M`;
}

export const signed = (n: number): string => (n > 0 ? `+${groupDigits(n)}` : groupDigits(n));

/** A cost, in the canonical resource order, skipping the zeroes. */
export interface CostEntry {
  readonly kind: (typeof RESOURCES)[number];
  readonly amount: number;
}

export function costEntries(cost: Partial<Resources>): readonly CostEntry[] {
  const out: CostEntry[] = [];
  for (const kind of RESOURCES) {
    const amount = cost[kind] ?? 0;
    if (amount !== 0) out.push({ kind, amount });
  }
  return out;
}

/** True when `have` covers every line of `cost`. */
export function canAfford(have: Resources, cost: Partial<Resources>): boolean {
  for (const kind of RESOURCES) if ((cost[kind] ?? 0) > have[kind]) return false;
  return true;
}

/** How many of `cost` a player can pay for. `Infinity` is not a thing here. */
export function affordableCount(have: Resources, unit: Partial<Resources>, cap: number): number {
  let limit = cap;
  for (const kind of RESOURCES) {
    const per = unit[kind] ?? 0;
    if (per <= 0) continue;
    const n = Math.floor(have[kind] / per);
    if (n < limit) limit = n;
  }
  return limit < 0 ? 0 : limit;
}

export const scaleCost = (cost: Partial<Resources>, n: number): Partial<Resources> => {
  const out: Record<string, number> = {};
  for (const kind of RESOURCES) {
    const v = cost[kind] ?? 0;
    if (v !== 0) out[kind] = v * n;
  }
  return out as Partial<Resources>;
};

const TITLE_WORD = /(^|[\s_-])([a-z])/g;

/** "crowned_skyclaw" -> "Crowned Skyclaw". Only for ids with no authored name. */
export const titleCase = (s: string): string =>
  s.replace(/_/g, " ").replace(TITLE_WORD, (_m, lead: string, c: string) => lead + c.toUpperCase());

export const MASTERY_NAME: readonly string[] = Object.freeze([
  "None", "Basic", "Advanced", "Expert",
]);
